import type { IconifyIcon } from '@iconify/types'
import type { JSX } from 'solid-js'
import { type Component, children, createMemo, mergeProps, Show, useContext } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import Icon from '~/components/Icon'
import ButtonGroupContext from '~/contexts/ButtonGroupContext'
import type { ButtonColorType, ButtonGroupPropsType, ButtonPropsType, ButtonSizeType, ButtonVariantType } from '~/types/Button'
import { controlSize } from '~/utils/controlSize'
import ButtonGroup from './ButtonGroup'
import Tooltip from './Tooltip'

/**
 * La altura sale de la escala común (`utils/controlSize`) y es **fija**: antes
 * la marcaban el padding y el `line-height`, así que el mismo `size` medía
 * distinto según llevara icono o texto y según la variante tuviera borde o no.
 * Un botón sin relleno vertical y con `items-center` mide siempre su talla.
 */
const sizeClasses = (size: ButtonSizeType): { default: string; iconOnly: string } => {
  const s = controlSize(size)
  return {
    default: `${s.padX} py-0 ${s.text} ${s.height}`,
    iconOnly: `p-0 ${s.text} ${s.height}`
  }
}
const variantClasses: Record<ButtonVariantType, Record<ButtonColorType, { base: string; hover: string; active: string; disabled: string }>> = {
  contained: {
    primary: {
      base: 'bg-primary-500 text-white border border-transparent',
      hover: 'hover:bg-primary-600',
      active: 'active:bg-primary-700',
      disabled: 'disabled:bg-primary-100 disabled:text-primary-300 dark:disabled:bg-primary-900/50 dark:disabled:text-slate-100/50'
    },
    secondary: {
      base: 'bg-slate-500 text-white border border-transparent',
      hover: 'hover:bg-slate-600',
      active: 'active:bg-slate-700',
      disabled: 'disabled:bg-slate-200 disabled:text-slate-50 dark:disabled:bg-slate-900/50 dark:disabled:text-slate-500/50'
    },
    success: {
      base: 'bg-green-500 text-white border border-transparent',
      hover: 'hover:bg-green-600',
      active: 'active:bg-green-700',
      disabled: 'disabled:bg-green-500/20 disabled:text-green-500/50 dark:disabled:bg-green-500/20 dark:disabled:text-green-500/50'
    },
    danger: {
      base: 'bg-red-500 text-white border border-transparent',
      hover: 'hover:bg-red-600',
      active: 'active:bg-red-700',
      disabled: 'disabled:bg-red-100 disabled:text-red-300 dark:disabled:bg-red-900/20 dark:disabled:text-red-700/30'
    },
    warning: {
      base: 'bg-amber-500 text-white border border-transparent',
      hover: 'hover:bg-amber-600',
      active: 'active:bg-amber-700',
      disabled: 'disabled:bg-amber-100 disabled:text-amber-300 dark:disabled:bg-amber-900/20 dark:disabled:text-amber-700/30'
    },
    info: {
      base: 'bg-blue-500 text-white border border-transparent',
      hover: 'hover:bg-blue-600',
      active: 'active:bg-blue-700',
      disabled: 'disabled:bg-blue-100 disabled:text-blue-300 dark:disabled:bg-blue-900/20 dark:disabled:text-blue-700/30'
    },
    default: {
      base: 'bg-inverse text-on-inverse border border-transparent',
      hover: 'hover:opacity-90',
      active: 'active:opacity-80',
      disabled: 'disabled:bg-slate-100 disabled:text-slate-300 dark:disabled:bg-slate-900/20 dark:disabled:text-slate-700/30'
    }
  },
  outlined: {
    primary: {
      base: 'border border-primary-500 text-primary-500',
      hover: 'hover:bg-primary-50/10 dark:hover:bg-primary-500/10',
      active: 'active:bg-primary-100 dark:active:bg-primary-500/20',
      disabled: 'disabled:border-primary-200 disabled:text-primary-200 dark:disabled:border-primary-700/30 dark:disabled:text-primary-700/30'
    },
    secondary: {
      base: 'border border-slate-500 text-slate-500',
      hover: 'hover:bg-slate-50 dark:hover:bg-slate-500/10',
      active: 'active:bg-slate-100 dark:active:bg-slate-500/20',
      disabled: 'disabled:border-slate-200 disabled:text-slate-200 dark:disabled:border-slate-700/30 dark:disabled:text-slate-700/30'
    },
    success: {
      base: 'border border-green-500 text-green-500',
      hover: 'hover:bg-green-50 dark:hover:bg-green-500/10',
      active: 'active:bg-green-100 dark:active:bg-green-500/20',
      disabled: 'disabled:border-green-200 disabled:text-green-200 dark:disabled:border-green-700/30 dark:disabled:text-green-700/30'
    },
    danger: {
      base: 'border border-red-500 text-red-500',
      hover: 'hover:bg-red-50 dark:hover:bg-red-500/10',
      active: 'active:bg-red-100 dark:active:bg-red-500/20',
      disabled: 'disabled:border-red-200 disabled:text-red-200 dark:disabled:border-red-700/30 dark:disabled:text-red-700/30'
    },
    warning: {
      base: 'border border-amber-500 text-amber-500',
      hover: 'hover:bg-amber-50 dark:hover:bg-amber-500/10',
      active: 'active:bg-amber-100 dark:active:bg-amber-500/20',
      disabled: 'disabled:border-amber-200 disabled:text-amber-200 dark:disabled:border-amber-700/30 dark:disabled:text-amber-700/30'
    },
    info: {
      base: 'border border-blue-500 text-blue-500',
      hover: 'hover:bg-blue-50 dark:hover:bg-blue-500/10',
      active: 'active:bg-blue-100 dark:active:bg-blue-500/20',
      disabled: 'disabled:border-blue-200 disabled:text-blue-200 dark:disabled:border-blue-700/30 dark:disabled:text-blue-700/30'
    },
    default: {
      base: 'border border-slate-800 text-foreground dark:border-slate-200',
      hover: 'hover:bg-slate-50 dark:hover:bg-slate-500/10',
      active: 'active:bg-slate-100 dark:active:bg-slate-500/20',
      disabled: 'disabled:border-slate-200 disabled:text-slate-200 dark:disabled:border-slate-700/30 dark:disabled:text-slate-700/30'
    }
  },
  text: {
    primary: {
      base: 'text-primary-500',
      hover: 'hover:text-primary-600 dark:hover:text-primary-500/80',
      active: 'active:bg-primary-100 dark:active:bg-primary-500/20',
      disabled: 'disabled:text-primary-300 dark:disabled:text-primary-600'
    },
    secondary: {
      base: 'text-foreground-subtle',
      hover: 'hover:text-foreground-muted',
      active: 'active:bg-slate-100 dark:active:bg-slate-500/20',
      disabled: 'disabled:text-slate-400 dark:disabled:text-slate-500'
    },
    success: {
      base: 'text-green-500',
      hover: 'hover:text-green-600 dark:hover:text-green-500/80',
      active: 'active:bg-green-500/10',
      disabled: 'disabled:text-green-400 dark:disabled:text-green-600'
    },
    danger: {
      base: 'text-red-500',
      hover: 'hover:text-red-600 dark:hover:text-red-500/80',
      active: 'active:bg-red-500/10',
      disabled: 'disabled:text-red-400 dark:disabled:text-red-600'
    },
    warning: {
      base: 'text-amber-500',
      hover: 'hover:text-amber-600 dark:hover:text-amber-500/80',
      active: 'active:bg-amber-500/10',
      disabled: 'disabled:text-amber-400 dark:disabled:text-amber-600'
    },
    info: {
      base: 'text-blue-500',
      hover: 'hover:text-blue-600 dark:hover:text-blue-500/80',
      active: 'active:bg-blue-500/10',
      disabled: 'disabled:text-blue-400 dark:disabled:text-blue-600'
    },
    default: {
      base: 'text-foreground',
      hover: 'hover:text-slate-900 dark:hover:text-slate-100',
      active: 'active:bg-slate-100 dark:active:bg-slate-500/10',
      disabled: 'disabled:text-slate-400 dark:disabled:text-slate-500'
    }
  },
  soft: {
    primary: {
      base: 'bg-primary-100 text-primary-700 dark:bg-primary-500/20 dark:text-primary-300',
      hover: 'hover:bg-primary-200 dark:hover:bg-primary-500/30',
      active: 'active:bg-primary-300 dark:active:bg-primary-500/40',
      disabled: 'disabled:bg-primary-50 disabled:text-primary-300 dark:disabled:bg-primary-500/5 dark:disabled:text-primary-700/30'
    },
    secondary: {
      base: 'bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-300',
      hover: 'hover:bg-slate-200 dark:hover:bg-slate-500/30',
      active: 'active:bg-slate-300 dark:active:bg-slate-500/40',
      disabled: 'disabled:bg-slate-50 disabled:text-slate-400 dark:disabled:bg-slate-500/5 dark:disabled:text-slate-700/30'
    },
    success: {
      base: 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300',
      hover: 'hover:bg-green-200 dark:hover:bg-green-500/30',
      active: 'active:bg-green-300 dark:active:bg-green-500/40',
      disabled: 'disabled:bg-green-50 disabled:text-green-300 dark:disabled:bg-green-500/5 dark:disabled:text-green-700/30'
    },
    danger: {
      base: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',
      hover: 'hover:bg-red-200 dark:hover:bg-red-500/30',
      active: 'active:bg-red-300 dark:active:bg-red-500/40',
      disabled: 'disabled:bg-red-50 disabled:text-red-300 dark:disabled:bg-red-500/5 dark:disabled:text-red-700/30'
    },
    warning: {
      base: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
      hover: 'hover:bg-amber-200 dark:hover:bg-amber-500/30',
      active: 'active:bg-amber-300 dark:active:bg-amber-500/40',
      disabled: 'disabled:bg-amber-50 disabled:text-amber-300 dark:disabled:bg-amber-500/5 dark:disabled:text-amber-700/30'
    },
    info: {
      base: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',
      hover: 'hover:bg-blue-200 dark:hover:bg-blue-500/30',
      active: 'active:bg-blue-300 dark:active:bg-blue-500/40',
      disabled: 'disabled:bg-blue-50 disabled:text-blue-300 dark:disabled:bg-blue-500/5 dark:disabled:text-blue-700/30'
    },
    default: {
      base: 'bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-300',
      hover: 'hover:bg-slate-200 dark:hover:bg-slate-500/30',
      active: 'active:bg-slate-300 dark:active:bg-slate-500/40',
      disabled: 'disabled:bg-slate-50 disabled:text-slate-400 dark:disabled:bg-slate-500/5 dark:disabled:text-slate-700/30'
    }
  }
}
const Button: Component<ButtonPropsType> & { Group: Component<ButtonGroupPropsType> } = (props): JSX.Element => {
  const groupDefaults = useContext(ButtonGroupContext) || {}

  const merged = mergeProps(
    {
      variant: (groupDefaults.variant ?? 'contained') as ButtonVariantType,
      color: (groupDefaults.color ?? 'primary') as ButtonColorType,
      size: (groupDefaults.size ?? 'md') as ButtonSizeType,
      type: 'button' as 'button' | 'submit' | 'reset',
      disabled: false,
      loading: false,
      pill: false,
      fullWidth: false,
      pressed: false
    },
    props
  )

  const isIconOnly = createMemo(() => Boolean(merged.icon && !merged.children && !merged.startIcon && !merged.endIcon && !merged.prependIcon && !merged.appendIcon))

  // Los hijos se resuelven con `children()` para saber si son texto plano: sólo
  // el texto plano se envuelve en un span con `truncate`, que es lo que permite
  // la elipsis cuando el botón se encoge (flex-1, filas apretadas). El icono
  // nunca encoge: `shrink-0` en cada Icon mantiene su tamaño fijo.
  const resolvedChildren = children(() => merged.children)
  const isPlainText = createMemo(() => {
    const value = resolvedChildren()
    if (typeof value === 'string') return true
    return Array.isArray(value) && value.every(item => typeof item === 'string' || typeof item === 'number')
  })

  const buttonClasses = createMemo(() => {
    const variantStyle = variantClasses[merged.variant][merged.color]
    const sizeStyle = isIconOnly() ? sizeClasses(merged.size).iconOnly : sizeClasses(merged.size).default
    return twMerge([
      'select-none cursor-pointer inline-flex items-center justify-center font-medium disabled:cursor-not-allowed min-w-0 truncate',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      merged.loading ? 'pointer-events-none' : '',
      sizeStyle,
      variantStyle.base,
      merged.variant === 'soft' ? 'border border-transparent' : '',
      variantStyle.hover,
      variantStyle.active,
      variantStyle.disabled,
      merged.fullWidth ? 'w-full' : '',
      isIconOnly() ? 'rounded-full aspect-square' : merged.pill ? 'rounded-full' : 'rounded-lg',
      merged.class
    ])
  })

  // El respaldo era el icono en blanco de mdi, que no existe en Iconify: se pedía a la API y
  // volvía un 404. Sólo se alcanzaba con un icono vacío —los tres usos están
  // dentro de un `Show` que ya comprueba que haya icono—, y con cadena vacía el
  // `Show` de `Icon` no pinta nada, que es lo que se pretendía.
  const getIconFromProps = (icon?: string | IconifyIcon): string | IconifyIcon => icon || ''
  const iconSizeForVariant = createMemo(() => merged.iconSize ?? controlSize(merged.size).icon)

  const renderButton = () => (
    <button
      type={merged.type}
      // El botón de enviar de un modal vive fuera del <form>; sin esto la
      // asociación se pierde y pulsarlo no hace absolutamente nada.
      form={merged.form}
      name={merged.name}
      value={merged.value}
      class={buttonClasses()}
      classList={merged.classList}
      disabled={merged.disabled}
      aria-busy={merged.loading ? 'true' : undefined}
      aria-disabled={merged.disabled || merged.loading ? 'true' : undefined}
      aria-label={props['aria-label'] || merged.tooltip || (isIconOnly() ? `${merged.children || 'Botón'}` : undefined)}
      aria-pressed={merged.pressed !== undefined ? merged.pressed : undefined}
      onClick={e => {
        if (!merged.loading && !merged.disabled && merged.onClick) {
          merged.onClick(e)
        }
      }}
      onKeyDown={e => {
        if (merged.loading || merged.disabled) {
          e.preventDefault()
          e.stopPropagation()
        }
      }}
      onKeyUp={e => {
        if (merged.loading || merged.disabled) {
          e.preventDefault()
          e.stopPropagation()
        }
      }}
      onMouseDown={e => {
        if (merged.loading || merged.disabled) {
          e.preventDefault()
          e.stopPropagation()
        }
      }}
      onMouseUp={e => {
        if (merged.loading || merged.disabled) {
          e.preventDefault()
          e.stopPropagation()
        }
      }}
    >
      <Show when={merged.loading}>
        <Icon icon="svg-spinners:ring-resize" size={iconSizeForVariant()} class={isIconOnly() ? 'shrink-0' : 'shrink-0 mr-2'} />
      </Show>
      <Show when={!merged.loading && (merged.startIcon || merged.prependIcon)}>
        <Icon icon={String(getIconFromProps(merged.startIcon || merged.prependIcon))} size={iconSizeForVariant()} class={merged.children ? 'shrink-0 mr-2' : 'shrink-0'} />
      </Show>
      <Show when={!merged.loading && merged.icon}>
        <Icon icon={String(getIconFromProps(merged.icon))} size={iconSizeForVariant()} class="shrink-0" />
      </Show>
      <Show when={isPlainText()} fallback={resolvedChildren()}>
        <span class="min-w-0 truncate">{resolvedChildren()}</span>
      </Show>
      <Show when={merged.endIcon || merged.appendIcon}>
        <Icon icon={String(getIconFromProps(merged.endIcon || merged.appendIcon))} size={iconSizeForVariant()} class={merged.children ? 'shrink-0 ml-2' : 'shrink-0'} />
      </Show>
    </button>
  )

  return (
    <Show when={merged.tooltip} fallback={renderButton()}>
      <Tooltip content={merged.tooltip!} placement="top">
        {renderButton()}
      </Tooltip>
    </Show>
  )
}

Button.Group = ButtonGroup

export default Button
